import { useResource } from "Infrastructure/Translation/Resources";
import { useOrderDetailQuery } from "Order/Api/Query/useOrderDetailQuery";
import { CallOutcomeCard } from "Order/Components/Call/Card";
import { OrderHeader } from "Order/Components/Layout/OrderHeader";
import { ClaimDrawer } from "Order/Modules/Claim/Components/ClaimDrawer";
import { useOrderExaminationByOrderIdQuery } from "Order/Modules/Examination/Api/Query/useOrderExaminationByOrderIdQuery";
import { OrderArchiveConfirmationModal } from "Order/Modules/Examination/Components/OrderArchiveConfirmationModal";
import { useGetOrderPriceByOrderIdQuery } from "Order/Modules/Price/Api/Query/useGetOrderPriceByOrderIdQuery";
import {
	getOrderDetailTabs,
	type OrderDetailTabs,
} from "Order/Pages/Detail/Tabs/Hooks/useOrderDetailTabs";
import { useOrderSetupIdQuery } from "Settings/Modules/OrderSetup/Api/Query/useOrderSetupIdQuery";
import { PageContainer } from "Shared/Components/Container";
import { Tabs } from "Shared/Components/Tabs";
import { getTabPanelElement } from "Shared/Components/Tabs/Helpers/getTabPanelElement";
import { useNavigateTabs } from "Shared/Components/Tabs/Hooks/useNavigateTabs";
import { Box, CircularProgress } from "@mui/material";
import { useParams } from "react-router";
import { CreateExaminationModal } from "../../Modules/Examination/Components/CreateExaminationModal";

type OrderDetailProps = {
	currentTab: OrderDetailTabs;
};

export function OrderDetailPage({ currentTab }: OrderDetailProps) {
	const { t } = useResource();
	const { id: orderId } = useParams();
	const paramsOrderId = orderId ? Number(orderId) : undefined;

	const { data: order, isLoading: isLoadingOrder } =
		useOrderDetailQuery(paramsOrderId);
	const { data: orderPrice } = useGetOrderPriceByOrderIdQuery(paramsOrderId);
	const { data: orderSetup } = useOrderSetupIdQuery(order?.orderSetupId);
	const { data: orderExamination } = useOrderExaminationByOrderIdQuery(
		paramsOrderId,
		!!order?.hasExamination,
	);

	const tabs = order?.id
		? getOrderDetailTabs(
				order?.id,
				t,
				order?.invoiceId,
				!!order?.hasExamination,
				order?.claims,
			)
		: null;

	return (
		<>
			{!order || isLoadingOrder ? (
				<Box width="100%" display="flex" justifyContent="center" mt={5}>
					<CircularProgress />
				</Box>
			) : (
				<PageContainer
					title="Order detail"
					description="Detailed information about the order"
				>
					<OrderHeader order={order} />
					{tabs && (
						<Tabs
							value={currentTab}
							tabs={tabs}
							mapItem={(tab) =>
								getTabPanelElement(
									tab,
									order,
									orderPrice,
									orderSetup,
									orderExamination,
								)
							}
							hookFunction={useNavigateTabs}
						/>
					)}
					<ClaimDrawer />
					<CallOutcomeCard
						phoneNumber={order.customer.phone}
						orderId={order.id}
					/>
					<CreateExaminationModal orderId={order.id} />
					<OrderArchiveConfirmationModal orderId={order.id} />
				</PageContainer>
			)}
		</>
	);
}
